<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { Plus } from "@lucide/vue";

import { Avatar, FormAlert, Button, Skeleton } from "@/shared/ui";
import { formatCents, formatDurationUk } from "@/shared/lib";

import type { AssignedMaster } from "../model/useServiceAssign";

defineProps<{
  assigned: AssignedMaster[];
  assignedStatus: "loading" | "ready" | "error" | "idle";
}>();

const emit = defineEmits<{
  addMaster: [];
  editMaster: [masterId: string];
  retry: [];
}>();

const { t } = useI18n();
</script>

<template>
  <div class="assigned-master-list" data-testid="assigned-master-list">
    <!-- Loading -->
    <template v-if="assignedStatus === 'loading'">
      <div
        v-for="n in 2"
        :key="n"
        class="assigned-master-list__skeleton-row"
        data-testid="assigned-skeleton"
      >
        <Skeleton class="assigned-master-list__skeleton-avatar" />
        <div class="assigned-master-list__skeleton-body">
          <Skeleton class="assigned-master-list__skeleton-name" />
          <Skeleton class="assigned-master-list__skeleton-meta" />
        </div>
      </div>
    </template>

    <!-- Error -->
    <template v-else-if="assignedStatus === 'error'">
      <FormAlert
        data-testid="assigned-error"
        variant="destructive"
        :message="t('services.detail.assignedError')"
      />
      <Button
        variant="outline"
        size="sm"
        data-testid="assigned-retry"
        @click="emit('retry')"
      >
        {{ t("services.detail.retry") }}
      </Button>
    </template>

    <!-- Ready -->
    <template v-else>
      <div
        v-for="master in assigned"
        :key="master.masterId"
        class="assigned-master-list__row"
        data-testid="assigned-master-row"
      >
        <!-- GAP 1: name prop → initials on deterministic colour bg -->
        <Avatar size="sm" :name="master.name" />

        <div class="assigned-master-list__info">
          <span
            class="assigned-master-list__name"
            data-testid="master-name"
          >{{ master.name }}</span>

          <!-- GAP 2 + GAP 4: humanised duration, accented price, muted buffer -->
          <span
            class="assigned-master-list__meta"
            data-testid="master-meta"
          >
            <b>{{ formatDurationUk(master.durationMin) }}</b>
            {{ " · " }}
            <b>{{ formatCents(master.priceCents) }}</b>
            {{ " · " }}{{ t("services.detail.master.bufferMeta", { buffer: master.bufferMin }) }}
          </span>
        </div>

        <!-- GAP 6: text-link, no button chrome -->
        <button
          type="button"
          class="assigned-master-list__edit"
          data-testid="edit-master-btn"
          @click="emit('editMaster', master.masterId)"
        >
          {{ t("services.detail.master.edit") }}
        </button>
      </div>

      <!-- GAP 5: full-width primary row with Plus icon + border-top -->
      <button
        type="button"
        class="assigned-master-list__add"
        data-testid="add-master-btn"
        @click="emit('addMaster')"
      >
        <Plus class="assigned-master-list__add-icon" aria-hidden="true" />
        {{ t("services.detail.addMoreMaster") }}
      </button>
    </template>
  </div>
</template>

<style scoped>
.assigned-master-list {
  display: flex;
  flex-direction: column;
}

.assigned-master-list__skeleton-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding-block: 0.75rem;
}

.assigned-master-list__skeleton-avatar {
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  flex-shrink: 0;
}

.assigned-master-list__skeleton-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.assigned-master-list__skeleton-name {
  height: 0.875rem;
  width: 60%;
}

.assigned-master-list__skeleton-meta {
  height: 0.75rem;
  width: 80%;
}

/* GAP 7: border-top separators between rows */
.assigned-master-list__row {
  display: flex;
  align-items: center;
  gap: 0.6875rem;
  padding: 0.6875rem 0.25rem;
  border-top: 1px solid var(--border);
}

.assigned-master-list__row:first-child {
  border-top: none;
}

.assigned-master-list__info {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.assigned-master-list__name {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--foreground);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* GAP 4: meta row — muted base, bold accents via <b> */
.assigned-master-list__meta {
  font-size: 0.75rem;
  color: var(--muted-foreground);
  margin-block-start: 0.0625rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.assigned-master-list__meta b {
  color: var(--foreground);
  font-weight: 600;
}

/* GAP 6: Змінити — text-link, no button chrome */
.assigned-master-list__edit {
  flex-shrink: 0;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--primary);
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;
  line-height: inherit;
}

.assigned-master-list__edit:focus-visible {
  outline: 2px solid var(--ring);
  outline-offset: 2px;
  border-radius: var(--radius-sm);
}

/* GAP 5: Додати майстра — full-width primary row */
.assigned-master-list__add {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.8125rem 0.25rem;
  border: none;
  border-top: 1px solid var(--border);
  background: none;
  color: var(--primary);
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  text-align: left;
}

.assigned-master-list__add:focus-visible {
  outline: 2px solid var(--ring);
  outline-offset: 2px;
  border-radius: var(--radius-sm);
}

.assigned-master-list__add-icon {
  width: 1.125rem;
  height: 1.125rem;
  flex-shrink: 0;
}
</style>
